import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { ago } from '../../shared/ui/format.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';
import { breadcrumb } from '../../shared/ui/breadcrumb.js';
import { overview } from '../api/developer.js';
import { PROFILE_PATH, word } from '../accounts.js';

/**
 * The person's own decision log: what covdbg decided about each of their runs, and why.
 *
 * Theirs alone - the endpoint asks for one principal's rows and nobody else's - and read from the
 * person's side: "free" and "granted" are both fine, and only what affects them reads as amber.
 */
export class UserProfileDecisions extends LiaDataElement
{
   static properties = { data: { state: true } };

   constructor()
   {
      super();
      this.data = null;
   }

   async load(signal)
   {
      this.data = await overview(signal);
   }

   render()
   {
      const rows = this.data?.decisions ?? [];

      return html`
         <lia-page
            title="Decisions"
            icon="fa-solid fa-list-check"
            description="What covdbg decided about each run under one of your addresses."
         >
            <div class="d-flex flex-column gap-3">
               ${breadcrumb([{ label: 'Your profile', url: PROFILE_PATH }, { label: 'Decisions' }])}
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}
               ${!this.data && !this.error ? loadingPanel('Loading your decisions') : nothing}
               ${this.data
                  ? html`
                     <lia-card>
                        <lia-data-table
                           listing-id="profile-decisions"
                           .perPage=${25}
                           no-column-manager
                           .columns=${[
                              { key: 'occurredAt', label: 'When', sortable: true, render: row => ago(row.occurredAt) },
                              { key: 'remoteKey', label: 'Repository', sortable: true, searchable: true, render: row => row.remoteKey || 'Unknown repository' },
                              {
                                 key: 'decision',
                                 label: 'Decision',
                                 sortable: true,
                                 render: row => html`<lia-badge variant=${decisionVariant(row.decision)}>${decisionWord(row.decision)}</lia-badge>`
                              },
                              { key: 'reason', label: 'Why', sortable: true, render: row => reasonWord(row.reason) },
                              { key: 'context', label: 'From', sortable: true, render: row => word(row.context) }
                           ]}
                           .rows=${rows}
                           .rowKey=${row => row.id}
                           empty-title="No decisions yet"
                           empty-message="Nothing has run under one of your addresses."
                        ></lia-data-table>
                     </lia-card>`
                  : nothing}
            </div>
         </lia-page>`;
   }
}

/**
 * What a decision looks like to the person it was about.
 *
 * Softer than the operator's colours on purpose: "free" and "granted" are both fine outcomes here, and
 * a developer reading a wall of green and grey should only see amber where something actually affects
 * them.
 */
function decisionVariant(decision)
{
   switch (decision)
   {
   case 'granted':
   case 'granted_free':
      return 'success';
   case 'granted_grace':
   case 'granted_degraded':
      return 'warning';
   case 'refused':
      return 'danger';
   default:
      return 'secondary';
   }
}

/** The reason, in the person's words: what about the run decided it. */
function reasonWord(reason)
{
   switch (reason)
   {
   case 'public_repository':
      return 'Public repository';
   case 'oss_grant':
      return 'Open-source grant';
   case 'seat_held':
      return 'Your seat';
   case 'bot_author':
      return 'Bot';
   case 'personal_repository':
      return 'Your personal repository';
   case 'personal_lock_elsewhere':
      return 'Personal use is locked to another repository';
   case 'unseated_author':
      return 'No seat in the team';
   case 'over_quota':
      return 'Team over quota';
   case 'grace_expired':
      return 'Grace period over';
   case 'sign_in_required':
      return 'Sign-in required';
   case 'not_in_team':
      return 'No seat in the team you signed in for';
   case 'token_scope':
      return 'Token limited to another repository';
   default:
      return word(reason);
   }
}

function decisionWord(decision)
{
   switch (decision)
   {
   case 'granted_free':
      return 'Free';
   case 'granted_grace':
      return 'Granted, over quota';
   case 'granted_degraded':
      return 'Report gated';
   case 'refused':
      return 'Refused';
   default:
      return 'Granted';
   }
}

customElements.define('user-profile-decisions', UserProfileDecisions);
